import { useState, useEffect, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import MovieCard from "@/components/feature/MovieCard";
import { categories, movies } from "@/mocks/movies";

const categoryBanners: Record<string, string> = {
  dongzuo: "https://readdy.ai/api/search-image?query=Cinematic%20action%20movie%20scene%20abstract%20background%2C%20intense%20and%20dynamic%20aesthetic%2C%20dark%20atmospheric%20lighting%20with%20warm%20orange%20accents%2C%20film%20genre%20vibe%2C%20wide%20composition&width=1600&height=700&seq=category-bg-dongzuo&orientation=landscape",
  xiju: "https://readdy.ai/api/search-image?query=Cinematic%20comedy%20movie%20scene%20abstract%20background%2C%20bright%20and%20playful%20aesthetic%2C%20warm%20lighting%20with%20soft%20yellow%20tones%2C%20film%20genre%20vibe%2C%20wide%20composition&width=1600&height=700&seq=category-bg-xiju&orientation=landscape",
  aiqing: "https://readdy.ai/api/search-image?query=Cinematic%20romance%20movie%20scene%20abstract%20background%2C%20soft%20and%20romantic%20aesthetic%2C%20warm%20pink%20and%20golden%20lighting%2C%20film%20genre%20vibe%2C%20wide%20composition&width=1600&height=700&seq=category-bg-aiqing&orientation=landscape",
  kehuan: "https://readdy.ai/api/search-image?query=Cinematic%20sci-fi%20movie%20scene%20abstract%20background%2C%20futuristic%20technology%20aesthetic%2C%20dark%20blue%20and%20neon%20lighting%2C%20film%20genre%20vibe%2C%20wide%20composition&width=1600&height=700&seq=category-bg-kehuan&orientation=landscape",
  kongbu: "https://readdy.ai/api/search-image?query=Cinematic%20horror%20movie%20scene%20abstract%20background%2C%20dark%20and%20eerie%20aesthetic%2C%20deep%20shadows%20and%20cold%20blue%20lighting%2C%20film%20genre%20vibe%2C%20wide%20composition&width=1600&height=700&seq=category-bg-kongbu&orientation=landscape",
  jilupian: "https://readdy.ai/api/search-image?query=Cinematic%20documentary%20movie%20scene%20abstract%20background%2C%20natural%20and%20authentic%20aesthetic%2C%20warm%20earthy%20tones%20and%20soft%20lighting%2C%20film%20genre%20vibe%2C%20wide%20composition&width=1600&height=700&seq=category-bg-jilupian&orientation=landscape",
  donghua: "https://readdy.ai/api/search-image?query=Cinematic%20animated%20movie%20scene%20abstract%20background%2C%20colorful%20and%20creative%20aesthetic%2C%20bright%20pastel%20lighting%2C%20film%20genre%20vibe%2C%20wide%20composition&width=1600&height=700&seq=category-bg-donghua&orientation=landscape",
  juqing: "https://readdy.ai/api/search-image?query=Cinematic%20drama%20movie%20scene%20abstract%20background%2C%20emotional%20and%20deep%20aesthetic%2C%20warm%20golden%20lighting%20with%20soft%20shadows%2C%20film%20genre%20vibe%2C%20wide%20composition&width=1600&height=700&seq=category-bg-juqing&orientation=landscape",
};

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const [activeCategory, setActiveCategory] = useState(slug || "dongzuo");
  const [sortBy, setSortBy] = useState<"rating" | "year" | "default">("default");

  useEffect(() => {
    if (slug) {
      setActiveCategory(slug);
    }
    window.scrollTo({ top: 0, behavior: "instant" as const });
  }, [slug]);

  const currentCategory = categories.find((c) => c.slug === activeCategory);

  const filteredMovies = useMemo(() => {
    return movies
      .filter((m) => m.categorySlug === activeCategory)
      .sort((a, b) => {
        if (sortBy === "rating") return b.rating - a.rating;
        if (sortBy === "year") return b.year - a.year;
        return 0;
      });
  }, [activeCategory, sortBy]);

  const bannerUrl = categoryBanners[activeCategory] || categoryBanners.dongzuo;

  const handleCategoryChange = (newSlug: string) => {
    setActiveCategory(newSlug);
    navigate(`/category/${newSlug}`);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  return (
    <>
      <Navbar />
      <main className="pt-20 min-h-screen bg-background-50">
        <div className="max-w-[1440px] mx-auto px-4 md:px-6 pt-6">
          <nav className="flex items-center gap-2 text-sm text-foreground-500 mb-6" aria-label="面包屑导航">
            <a
              href="/"
              onClick={(e) => {
                e.preventDefault();
                navigate("/");
                window.scrollTo({ top: 0, behavior: "smooth" });
              }}
              className="hover:text-primary-500 transition-colors"
            >
              首页
            </a>
            <i className="ri-arrow-right-s-line text-xs"></i>
            <span className="text-foreground-700 font-medium">
              {currentCategory?.name || "电影分类"}
            </span>
          </nav>
        </div>

        <section className="relative w-full h-[260px] md:h-[360px] overflow-hidden">
          <img
            src={bannerUrl}
            alt={currentCategory?.name || "电影分类"}
            title={`${currentCategory?.name || ""} - 青柠影院高清在线观看`}
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/25 to-background-50"></div>
          <div className="absolute bottom-0 left-0 right-0 p-6 md:p-10">
            <div className="max-w-[1440px] mx-auto">
              <h1 className="text-2xl md:text-4xl font-bold text-white">
                {currentCategory?.name || "电影分类"}
              </h1>
              <p className="text-white/70 text-sm md:text-base mt-2">
                探索精彩的{currentCategory?.name || "电影"}世界
              </p>
            </div>
          </div>
        </section>

        <section className="bg-background-50 border-b border-background-200 sticky top-16 z-40">
          <div className="max-w-[1440px] mx-auto px-4 md:px-6">
            <div className="flex items-center gap-1 overflow-x-auto py-3" style={{ scrollbarWidth: "none" }}>
              {categories.map((cat) => (
                <button
                  key={cat.slug}
                  onClick={() => handleCategoryChange(cat.slug)}
                  className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-all duration-200 cursor-pointer ${
                    activeCategory === cat.slug
                      ? "bg-primary-500 text-white"
                      : "text-foreground-600 hover:text-foreground-950 hover:bg-background-200"
                  }`}
                >
                  <i className={`${cat.icon} mr-1.5 text-xs`}></i>
                  {cat.name}
                </button>
              ))}
            </div>
          </div>
        </section>

        <section className="max-w-[1440px] mx-auto px-4 md:px-6 py-6">
          <div className="flex items-center justify-between flex-wrap gap-3">
            <p className="text-sm text-foreground-600">
              共找到 <strong className="text-foreground-950">{filteredMovies.length}</strong> 部
              {currentCategory?.name || "电影"}
            </p>
            <div className="flex items-center gap-2">
              <span className="text-xs text-foreground-500">排序：</span>
              {[
                { key: "default" as const, label: "默认" },
                { key: "rating" as const, label: "评分最高" },
                { key: "year" as const, label: "最新上映" },
              ].map((opt) => (
                <button
                  key={opt.key}
                  onClick={() => setSortBy(opt.key)}
                  className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors cursor-pointer whitespace-nowrap ${
                    sortBy === opt.key
                      ? "bg-primary-50 text-primary-600"
                      : "text-foreground-500 hover:text-foreground-700 hover:bg-background-200"
                  }`}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>
        </section>

        <section className="max-w-[1440px] mx-auto px-4 md:px-6 pb-16">
          {filteredMovies.length > 0 ? (
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4 md:gap-5">
              {filteredMovies.map((movie) => (
                <MovieCard key={movie.id} movie={movie} />
              ))}
            </div>
          ) : (
            <div className="text-center py-20">
              <div className="w-20 h-20 mx-auto flex items-center justify-center rounded-full bg-background-200">
                <i className="ri-film-line text-3xl text-foreground-400"></i>
              </div>
              <h3 className="mt-5 text-lg font-semibold text-foreground-700">
                暂无{currentCategory?.name || ""}电影
              </h3>
              <p className="mt-2 text-sm text-foreground-500">
                该分类下暂时没有电影内容，请查看其他分类
              </p>
              <button
                onClick={() => handleCategoryChange("dongzuo")}
                className="mt-4 px-5 py-2 bg-primary-500 hover:bg-primary-600 text-white text-sm font-medium rounded-full transition-colors cursor-pointer"
              >
                去动作片看看
              </button>
            </div>
          )}
        </section>
      </main>
      <Footer />

      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "CollectionPage",
            name: `${currentCategory?.name || ""} - 青柠影院`,
            description: `青柠影院${currentCategory?.name || ""}频道，提供最新${currentCategory?.name || ""}在线观看。`,
            isPartOf: {
              "@type": "WebSite",
              name: "青柠影院",
            },
          }),
        }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
              {
                "@type": "ListItem",
                position: 1,
                name: "首页",
              },
              {
                "@type": "ListItem",
                position: 2,
                name: currentCategory?.name || "电影分类",
              },
            ],
          }),
        }}
      />
    </>
  );
}